import { z } from "zod";

const count = z.number().int().nonnegative();
const optionalNumber = z.number().nullish();
const severity = z.enum(["ok", "degraded", "critical"]);
// Consumers ignore unknown additive health fields. No free-form vendor errors or logs.
export const edgeHealthSchema = z.object({
	capacity: z
		.object({
			bandwidthGuardThresholdBytesPerS: optionalNumber,
			bandwidthUsedBytesPerS: optionalNumber,
			currentStreamCount: count,
			maxStreamCount: count,
			oldestSegmentAgeSeconds: optionalNumber,
			segmentQueueDepth: count,
		})
		.nullish(),
	capturedAt: z.iso.datetime(),
	device: z
		.object({
			buildSha: z.string().max(128).nullish(),
			containerRestartCounts: z.record(z.string().max(64), count).optional(),
			cpuPressurePct: optionalNumber,
			lastBootReason: z.string().max(128).nullish(),
			memoryPressurePct: optionalNumber,
			ntpOffsetSeconds: optionalNumber,
			socTemperatureC: optionalNumber,
			uptimeSeconds: count,
			volumes: z
				.array(
					z.object({
						freeBytes: count,
						mountpoint: z.string().max(256),
						pressurePct: z.number().min(0).max(100),
						totalBytes: count,
					}),
				)
				.max(32)
				.optional(),
			watchdogReboots: count.optional(),
		})
		.nullish(),
	deviceId: z.string().min(1).max(128),
	schemaVersion: z.literal(1),
	severity: z
		.object({
			components: z.partialRecord(
				z.enum(["worker", "storage", "network", "streams", "clock"]),
				severity,
			),
			overall: severity,
			version: z.literal(1),
		})
		.nullish(),
	streams: z
		.array(
			z.object({
				configId: z.string().max(128),
				connected: z.boolean(),
				deliveryLatencyP50Ms: optionalNumber,
				deliveryLatencyP95Ms: optionalNumber,
				deliverySuccessRate: optionalNumber,
				ffmpegRestartCount: count.optional(),
				lastSegmentAt: z.iso.datetime().nullish(),
				segmentErrorCount: count.optional(),
			}),
		)
		.max(200)
		.nullish(),
});
export const edgeRuntimeReportSchema = z
	.object({
		appliedRevision: z.uuid().nullish(),
		attemptedRevision: z.uuid().nullish(),
		errorCode: z
			.enum([
				"validation",
				"unsupported_schema",
				"capacity",
				"dependency_unavailable",
				"apply_failed",
				"recovery_failed",
				"adoption_required",
			])
			.nullish(),
		health: edgeHealthSchema.optional(),
		schemaVersion: z.literal(1),
		sequence: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
		state: z.enum([
			"unconfigured",
			"applying",
			"applied",
			"rejected",
			"retrying",
			"recovery_pending",
		]),
	})
	.strict();
